import { useCallback, useEffect, useState } from "react";
import { adminApi } from "../../../lib/api";
import { toast } from "sonner";

/**
 * Single source of truth for the commercial dashboard data.
 *
 * Returns the 7 datasets (summary, timeline, vendedores, marcas, productos,
 * syncStatus, digestLast) plus convenience setters and a manual refetch.
 *
 * Refetches automatically whenever `qs` (period+source) changes, with a
 * cancellation flag so stale responses don't overwrite newer state.
 */
export function useCommercialData(qs) {
  const [data, setData] = useState({
    summary: null,
    timeline: [],
    vendedores: [],
    marcas: [],
    productos: [],
    syncStatus: null,
    digestLast: null,
  });
  const [loading, setLoading] = useState(true);

  const _fetchAll = useCallback(async (signal) => {
    const [s, t, v, m, p, st, dh] = await Promise.all([
      adminApi.get(`/admin/commercial/summary?${qs}`),
      adminApi.get(`/admin/commercial/timeline?${qs}`),
      adminApi.get(`/admin/commercial/vendedores?${qs}`),
      adminApi.get(`/admin/commercial/marcas?${qs}`),
      adminApi.get(`/admin/commercial/productos?${qs}`),
      adminApi.get(`/admin/commercial/sync-status`),
      adminApi.get(`/admin/commercial/digest/history`),
    ]);
    if (signal?.cancelled) return;
    setData({
      summary: s.data,
      timeline: t.data.series || [],
      vendedores: v.data.items || [],
      marcas: m.data.items || [],
      productos: p.data.items || [],
      syncStatus: st.data,
      digestLast: (dh.data.items || [])[0] || null,
    });
  }, [qs]);

  useEffect(() => {
    const signal = { cancelled: false };
    Promise.resolve().then(() => {
      if (signal.cancelled) return;
      setLoading(true);
      _fetchAll(signal)
        .catch(() => { if (!signal.cancelled) toast.error("No pudimos cargar el panel comercial"); })
        .finally(() => { if (!signal.cancelled) setLoading(false); });
    });
    return () => { signal.cancelled = true; };
  }, [_fetchAll]);

  const refetch = useCallback(() => _fetchAll(null), [_fetchAll]);

  const setSyncStatus = useCallback((syncStatus) => {
    setData((d) => ({ ...d, syncStatus }));
  }, []);

  const setDigestLast = useCallback((digestLast) => {
    setData((d) => ({ ...d, digestLast }));
  }, []);

  return { ...data, loading, refetch, setSyncStatus, setDigestLast };
}

/**
 * Fetches a single vendor's drill-down detail. Returns `null` when no vendor
 * is selected. Re-fetches when either `vendedorId` or `qs` changes.
 */
export function useVendorDetail(vendedorId, qs) {
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!vendedorId) {
      Promise.resolve().then(() => setDetail(null));
      return;
    }
    let alive = true;
    adminApi.get(`/admin/commercial/vendedor/${vendedorId}?${qs}`)
      .then((r) => { if (alive) setDetail(r.data); })
      .catch(() => { if (alive) toast.error("No se pudo cargar el detalle"); });
    return () => { alive = false; };
  }, [vendedorId, qs]);

  return detail;
}
